/* eslint-disable import/no-cycle */
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import useFetch from '@hooks/useFetch';
import { Spacings, mediaQuery } from '@styles/index';
import { useDispatch, useSelector } from 'react-redux';
import H4 from '@base/H4/H4';
import {
  getEnrolledCoursesFeed,
  getMoreCoursesFeed,
  getTopicInfoForCoursesPage
} from '@selectors/syllabus';
import { TOPIC_SYLLABUS_PAGE } from '@constants/segment';
import { fetchPracticeAvailableLanguages } from '@actions/practiceSession';
import MoreCourses from '@cont/PlusCourses/MoreCourses';
import { getNoFiltersKey } from '@cont/PlusCourses/syllabusHelper';
import { getIsLoggedIn } from '@selectors/auth';
import { getIsSubscribedToGoal } from '@selectors/subscription';
import pushRoute from '@utils/pushRoute';
import { useIsMobile } from '@stores/AppCommonData';
import { useRouter } from 'next/router';
import { redirectToRoute } from '@utils/route';
import EnrolledCourses from './enrollments/EnrolledCourses';
import NotesCard from './NotesCard';
import ChapterInfo from './chapterInfo/ChapterInfo';
import SideNav from './SideNav';
import { getTopicInfoEndpoint } from '../constants/apiHelper';
import Header from './Header';
import getTopicGroupAssetObj from '../constants/getTopicGroupAssetObj';
import PreSubscriptionBanner from './PreSubscriptionBanner';
import MissingChaptersNudge from './MissingChaptersNudge';
import {
  getIsChapterMissing,
  getIsSyllabusPageEducatorSpecific
} from '../utils';

export interface ISyllabusTopicPageProps {
  goalUID: string;
  goalSlug: string;
  topicUID: string;
}

const Wrapper = styled.div`
  grid-column: inherit;
  pointer-events: ${({
    // @ts-ignore
    disabled
  }) => (disabled ? 'none' : 'auto')};
`;

const ChaptersInfo = styled.div`
  display: grid;
  grid-template-columns: 1.7fr 4fr;
  column-gap: ${Spacings.SPACING_16B};
  ${mediaQuery.uptoMobile} {
    grid-template-columns: 4fr;
  }
`;

const BlockWrapper = styled.div`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-gap: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_14B};
`;

const MoreCoursesWrapper = styled(BlockWrapper)`
  margin-top: ${Spacings.SPACING_20B};
  ${mediaQuery.uptoMobile} {
    grid-template-columns: repeat(10, 1fr);
    grid-column-gap: ${Spacings.SPACING_2B};
    grid-row-gap: 0;
    grid-column: span 10;
    position: relative;
    & > div[orientation='vertical'] {
      margin-bottom: ${Spacings.SPACING_6B};
    }
  }
`;

const Title = styled(H4)`
  color: var(--color-text-primary);
  margin-bottom: ${Spacings.SPACING_2B};
  padding: 0;
  grid-column: span 18;
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0;
    grid-column: span 10;
  }
`;

const ChaptersWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_20B};
  &:last-child {
    margin-bottom: 0;
  }
`;

const Concepts = styled.div`
  margin-top: ${Spacings.SPACING_10B};
`;

const CHAPTERS_WRAPPER_MARGIN_BOTTOM = 80;
const HEADER_HEIGHT = 120;
const MORE_COURSES_MARGIN_TOP = 80;

const TopicPage: React.FC<ISyllabusTopicPageProps> = ({
  goalUID,
  goalSlug,
  topicUID
}) => {
  const { query } = useRouter();
  const { educatorUID } = query;
  const coursesRef = React.useRef<HTMLDivElement>(null);
  const chaptersRef = React.useRef<HTMLDivElement>(null);
  const endpoint = getTopicInfoEndpoint(goalUID, topicUID, educatorUID);
  const { data = {}, isError } = useFetch(
    endpoint,
    {},
    { revalidateOnMount: false }
  );
  const { data: topicInfo = {}, meta = {} } = data;
  const { missingConceptText, title: metaTitle, description, ctaText } = meta;
  const {
    children = [],
    title,
    areNotesEnabled,
    topicGroup = [],
    hasNotes,
    author
  } = topicInfo;
  const { title: topicGroupTitle = title } = topicGroup[0] || {};
  const { color } = getTopicGroupAssetObj(topicGroupTitle);
  const isSubscribed = useSelector(getIsSubscribedToGoal(goalUID));
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isMobile = useIsMobile();
  const [disable, setDisable] = React.useState(false);
  const showNotes = areNotesEnabled && hasNotes;

  const isSyllabusPageEducatorSpecific = getIsSyllabusPageEducatorSpecific(
    educatorUID,
    author
  );
  const showMissingChaptersNudge =
    isSyllabusPageEducatorSpecific && getIsChapterMissing(children);

  const handleGetSubscription = () => {
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goalSlug}/${goalUID}/subscribe`
    );
  };

  // Enrollments Section
  const topicTopology = useSelector((state) =>
    // @ts-ignore
    getTopicInfoForCoursesPage(state, topicUID)
  );
  const feedBlock = useSelector((state) =>
    getEnrolledCoursesFeed(state, topicTopology.uid)
  );

  // More Courses Section
  const noFiltersKey = getNoFiltersKey(topicTopology.uid);
  const moreCoursesAllFeed = useSelector((state) =>
    getMoreCoursesFeed(state, noFiltersKey)
  );
  const areMoreCoursesAvailable =
    moreCoursesAllFeed.results.length > 0 || moreCoursesAllFeed.isFetching;

  const scrollToCourses = () => {
    window.scrollTo({
      top:
        coursesRef.current?.offsetTop -
        (HEADER_HEIGHT + MORE_COURSES_MARGIN_TOP),
      behavior: 'smooth'
    });
  };

  const getHeightOfPreviousChildren = (currentChild) => {
    const children = Array.from(chaptersRef.current.children);

    let height = 0;
    [...Array(currentChild).keys()].forEach((element) => {
      height += children[element].clientHeight + CHAPTERS_WRAPPER_MARGIN_BOTTOM;
    });
    return height;
  };

  const scrollToChapter = (i) => {
    window.scrollTo({
      top: getHeightOfPreviousChildren(i) + HEADER_HEIGHT,
      behavior: 'smooth'
    });
  };

  const dispatch = useDispatch();
  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchPracticeAvailableLanguages(goalUID));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    if (isError) redirectToRoute(false, '', '/404', '/404');
  }, [isError]);

  if (isError) return null;

  return (
    <Wrapper
      // @ts-ignore
      disabled={disable}
    >
      <Header
        goalUID={goalUID}
        title={title}
        addMargin={children.length === 0}
        topicGroupTitle={topicGroupTitle}
        author={author}
      />
      {children.length > 0 && (
        <ChaptersInfo>
          {!isMobile && (
            <SideNav
              chaptersList={children}
              goalUID={goalUID}
              areMoreCoursesAvailable={areMoreCoursesAvailable}
              scrollToCourses={scrollToCourses}
              scrollToChapter={scrollToChapter}
              isSyllabusPageEducatorSpecific={isSyllabusPageEducatorSpecific}
            />
          )}
          <Concepts>
            {(!isLoggedIn || !isSubscribed) && (
              <PreSubscriptionBanner
                title="Get access to all videos"
                description="Get 800+hrs of full syllabus lectures with subscription"
                handleButtonClick={handleGetSubscription}
              />
            )}
            {showNotes && !isSyllabusPageEducatorSpecific && (
              <NotesCard marginBottom={Spacings.SPACING_6B} />
            )}
            <div ref={chaptersRef}>
              {children.map((chapter, index) => {
                return (
                  <ChaptersWrapper key={chapter.uid}>
                    <ChapterInfo
                      info={chapter}
                      index={index + 1}
                      showNotes={!areNotesEnabled}
                      topicTitle={title}
                      topicUID={topicUID}
                      goalUID={goalUID}
                      color={color}
                      disableTopicPage={setDisable}
                      missingConceptText={missingConceptText}
                      isSyllabusPageEducatorSpecific={
                        isSyllabusPageEducatorSpecific
                      }
                    />
                  </ChaptersWrapper>
                );
              })}
            </div>
            {showMissingChaptersNudge && <MissingChaptersNudge meta={meta} />}
          </Concepts>
        </ChaptersInfo>
      )}
      {!isSyllabusPageEducatorSpecific && (
        <EnrolledCourses topic={topicTopology} ref={coursesRef} />
      )}
      {areMoreCoursesAvailable && !isSyllabusPageEducatorSpecific && (
        <MoreCoursesWrapper ref={!feedBlock.results.length ? coursesRef : null}>
          <Title as="h2">More courses</Title>
          <MoreCourses
            // @ts-ignore
            topic={topicTopology}
            isContentAvailable={false}
            goalUID={goalUID}
            gridNumber={24}
            lastPrimarySource={TOPIC_SYLLABUS_PAGE}
            lastPrimarySourceSection="Topic Page - More Courses"
          />
        </MoreCoursesWrapper>
      )}
    </Wrapper>
  );
};

export default TopicPage;
